// src/features/records/components/RecordDetail/RecordDetail.js
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import RecordDetailErrorBoundary from './RecordDetailErrorBoundary';
import ModalHeader from './components/ModalHeader';
import HeroSection from './components/HeroSection';
import RecordContent from './components/RecordContent';
import RecordAttributes from './components/RecordAttributes';
import RecordComments from './components/RecordComments';
import RecordActions from '../shared/RecordActions';
import RecordAttachments from '../shared/RecordAttachments';
import AttachmentSkeleton from '../shared/AttachmentSkeleton';
import LinkedRecord from '../shared/LinkedRecord';
import MediaLightbox from '../GalleryView/components/MediaLightbox';
import { useRecord } from '../../hooks/useRecord';
import useRecordAccess from './hooks/useRecordAccess';
import { useFocusTrap } from '../../../../hooks';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import { DISPLAY_MODE, VISIBILITY } from '../../constants';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';
import styles from './RecordDetail.module.css';

const RecordDetailContent = ({ 
  recordId, 
  mode = DISPLAY_MODE.MODAL,
  initialData = null,
  initialOpenComments = false,
  initialFocusTarget = null,
  suspendModalInteractions = false,
  onClose,
  onEdit,
  onDelete,
  onShare,
  onLinkToRecord
}) => {
  const { t } = useTranslation();
  const { 
    record,
    attachments,
    attachmentsState,
    recordError,
    hydrateAttachments,
    loading: recordLoading,
    expectedAttachmentCount,
    attachmentPreviews
  } = useRecord(recordId, {
    hydrate: 'onMount',
    realtime: true // Enable real-time updates for detail view
  });

  // Use record directly or fall back to initialData
  const displayRecord = record || initialData;

  // Compute loading and error states
  const loading = recordLoading || (!displayRecord && !recordError);
  const error = recordError;

  // Simple retry function
  const retry = () => {
    if (attachmentsState === 'error' || !record) {
      hydrateAttachments();
    }
  };

  const {
    canView,
    canEdit,
    canComment,
    visibility
  } = useRecordAccess(displayRecord);

  const hasFocusTarget = Boolean(initialFocusTarget?.commentId || initialFocusTarget?.threadRootId);
  const [isCommentsOpen, setIsCommentsOpen] = useState(Boolean(initialOpenComments || hasFocusTarget));
  const [shouldScrollToComments, setShouldScrollToComments] = useState(
    Boolean(initialOpenComments && !hasFocusTarget)
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);

  useEffect(() => {
    const shouldOpenComments = Boolean(initialOpenComments || hasFocusTarget);
    setIsCommentsOpen(shouldOpenComments);
    setShouldScrollToComments(Boolean(initialOpenComments && !hasFocusTarget));
  }, [recordId, initialOpenComments, hasFocusTarget]);

  useEffect(() => {
    setIsLightboxOpen(false);
    setLightboxItems([]);
    setLightboxInitialIndex(0);
  }, [recordId]);

  const isModal = mode === DISPLAY_MODE.MODAL;
  const interactionsEnabled = isModal && !suspendModalInteractions && !isLightboxOpen;
  const isSuspended = isModal && (suspendModalInteractions || isLightboxOpen);

  // Focus trap for modal accessibility
  const modalRef = useFocusTrap(interactionsEnabled, {
    initialFocus: true,
    restoreFocus: !isSuspended
  });

  // Handle ESC key for modal
  useEffect(() => {
    if (interactionsEnabled && onClose) {
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [interactionsEnabled, onClose]);

  // Handle body scroll lock for modal
  useBodyScrollLock(isModal);

  useEffect(() => {
    if (!isCommentsOpen || !shouldScrollToComments) return;

    const target = document.getElementById(`record-comments-${recordId}`);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const composer = target.querySelector('[data-comment-composer="root"]');
    if (composer) {
      composer.focus();
    }
    setShouldScrollToComments(false);
  }, [isCommentsOpen, shouldScrollToComments, recordId, record]);

  // Loading state
  if (loading && !displayRecord) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.loadingIcon} size={48} />
        <p className={styles.loadingText}>
          {t('records.detail.loading', 'Loading record...')}
        </p>
      </div>
    );
  }

  // Error states
  if (error) {
    const errorKey = mapRecordsErrorToKey(error, {
      fallbackKey: 'records.detail.errors.load_failed'
    });
    const isNotFoundError = errorKey === 'records.errors.not_found';
    const errorTitle = isNotFoundError
      ? t('records.detail.errors.not_found_title', 'Record not found')
      : t('records.detail.errors.load_title', 'Unable to load record');
    const errorMessage = isNotFoundError
      ? t(
        'records.detail.errors.not_found_description',
        'This record may have been deleted or you may not have permission to view it.'
      )
      : t(errorKey, t('records.detail.errors.load_failed', 'There was a problem loading this record. Please try again.'));

    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h2 className={styles.errorTitle}>{errorTitle}</h2>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <button 
            className={styles.errorRetryButton}
            onClick={retry}
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  // Access denied
  if (displayRecord && !canView) {
    return (
      <div className={styles.accessDeniedContainer}>
        <div className={styles.accessDeniedContent}>
          <AlertCircle className={styles.accessDeniedIcon} size={48} />
          <h2 className={styles.accessDeniedTitle}>
            {visibility === VISIBILITY.PRIVATE 
              ? t('records.detail.access_denied.private_title', 'This record is private')
              : t(
                'records.detail.access_denied.friends_title',
                'This record is only visible to friends'
              )}
          </h2>
          <p className={styles.accessDeniedMessage}>
            {t(
              'records.detail.access_denied.description',
              "You don't have permission to view this record."
            )}
          </p>
        </div>
      </div>
    );
  }

  // Main content
  if (!displayRecord) return null;

  const scrollToComments = () => {
    const target = document.getElementById(`record-comments-${recordId}`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const composer = target.querySelector('[data-comment-composer="root"]');
    if (composer) {
      composer.focus();
    }
  };

  const handleCommentOpen = () => {
    if (isCommentsOpen) {
      scrollToComments();
      return;
    }
    setIsCommentsOpen(true);
    setShouldScrollToComments(true);
  };

  const getMediaId = (attachment) => {
    const baseId = attachment?.id || attachment?.storagePath || attachment?.metadata?.filename || 'unknown';
    return `record-detail:${recordId}:${baseId}`;
  };

  const handleMediaClick = (attachment) => {
    if (!attachments || attachments.length === 0) return;
    const snapshotItems = attachments
      .filter((item) => item?.type === 'image' || item?.type === 'video')
      .map((item) => ({
        ...item,
        id: getMediaId(item),
        attachmentId: item?.id
      }));

    if (snapshotItems.length === 0) return;
    const clickedId = getMediaId(attachment);
    const clickedIndex = snapshotItems.findIndex((item) => item.id === clickedId);
    setLightboxItems(snapshotItems);
    setLightboxInitialIndex(clickedIndex >= 0 ? clickedIndex : 0);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setLightboxItems([]);
  };

  const content = (
    <div 
      className={`${styles.recordDetail} ${styles[mode]}`}
      role={isModal ? "dialog" : undefined}
      aria-modal={interactionsEnabled ? "true" : undefined}
      aria-labelledby={isModal && displayRecord?.content?.title ? "record-title" : undefined}
      aria-hidden={isSuspended ? "true" : undefined}
      inert={isSuspended ? "" : undefined}
      style={isSuspended ? { pointerEvents: 'none' } : undefined}
      ref={isModal ? modalRef : undefined}
    >
      {/* Loading attachments indicator */}
      {attachmentsState === 'loading' && (
        <div className={styles.syncIndicator}>
          <Loader2 className={styles.syncIcon} size={16} />
          <span>{t('records.detail.attachments.loading', 'Loading attachments...')}</span>
        </div>
      )}

      {/* Modal Header (only in modal mode) */}
      {mode === DISPLAY_MODE.MODAL && (
        <ModalHeader
          record={displayRecord}
          onClose={onClose}
          onEdit={onEdit}
          onDelete={onDelete}
          onShare={onShare}
          canEdit={canEdit}
        />
      )}

      <div className={styles.scrollContainer}>
        {/* Hero Section - Fixed design with author, title, tags */}
        <HeroSection
          record={displayRecord}
          mode={mode}
          onEdit={onEdit}
          onDelete={onDelete}
          onShare={onShare}
          canEdit={canEdit}
          titleId={mode === DISPLAY_MODE.MODAL ? "record-title" : undefined}
        />

        {/* Primary Content Area - Typography-first */}
        <RecordContent record={displayRecord} />

        {/* Attachments Section - Media gallery */}
        {expectedAttachmentCount > 0 && (
          <div className={styles.attachmentsSection}>
            <h3 className={styles.attachmentsHeader}>
              {t('records.detail.attachments.title_with_count', {
                count: attachmentsState === 'ready' && attachments.length > 0
                  ? attachments.length
                  : expectedAttachmentCount,
                defaultValue: 'Attachments ({{count}})'
              })}
            </h3>
            <div className={styles.attachmentsGrid}>
              {attachmentsState === 'ready' && attachments.length > 0 ? (
                <RecordAttachments 
                  attachments={attachments} 
                  variant="grid"
                  onMediaClick={handleMediaClick}
                  getMediaId={getMediaId}
                />
              ) : attachmentsState === 'loading' ? (
                <AttachmentSkeleton 
                  previews={attachmentPreviews}
                  count={expectedAttachmentCount}
                  variant="grid"
                />
              ) : attachmentsState === 'error' ? (
                <div className={styles.attachmentError}>
                  <AlertCircle size={20} />
                  <span>{t('records.detail.attachments.load_failed', "Couldn't load attachments")}</span>
                  <button onClick={retry} className={styles.retryButton}>
                    {t('common.retry', 'Retry')}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Attributes Grid - Secondary info */}
        {displayRecord.attributes && Object.keys(displayRecord.attributes).length > 0 && (
          <div className={styles.attributesSection}>
            <RecordAttributes attributes={displayRecord.attributes} />
          </div>
        )}

        {/* Linked Records Section - Relationships */}
        {displayRecord.links?.outgoing?.length > 0 && (
          <div className={styles.linkedSection}>
            <h3 className={styles.sectionTitle}>
              {t('records.detail.linked_records.section_title', 'Linked records')}
            </h3>
            {displayRecord.links.outgoing.map((link, index) => (
              <LinkedRecord key={index} link={link} />
            ))}
          </div>
        )}

        {/* Interaction Section - Social engagement */}
        <div className={styles.interactionBar}>
          <RecordActions
            recordId={displayRecord.id}
            onLinkToRecord={onLinkToRecord}
            onComment={handleCommentOpen}
          />
        </div>

        {/* Comments Thread */}
        {isCommentsOpen ? (
          <RecordComments 
            recordId={displayRecord.id} 
            canComment={canComment}
            initialFocusTarget={initialFocusTarget}
          />
        ) : null}
      </div>
    </div>
  );

  // For modal mode, use portal
  if (isModal) {
    return createPortal(
      <>
        {!isLightboxOpen && (
          <div
            className={styles.modalOverlay}
            onClick={interactionsEnabled ? onClose : undefined}
            aria-hidden={isSuspended ? "true" : undefined}
            style={isSuspended ? { pointerEvents: 'none' } : undefined}
            data-testid="record-detail-overlay"
          />
        )}
        {content}
        <MediaLightbox
          items={lightboxItems}
          initialIndex={lightboxInitialIndex}
          isOpen={isLightboxOpen}
          onClose={handleLightboxClose}
        />
      </>,
      document.body
    );
  }

  // For page mode, render directly
  return (
    <>
      {content}
      <MediaLightbox
        items={lightboxItems}
        initialIndex={lightboxInitialIndex}
        isOpen={isLightboxOpen}
        onClose={handleLightboxClose}
      />
    </>
  );
};

// Main component with error boundary
const RecordDetail = (props) => {
  return (
    <RecordDetailErrorBoundary
      onError={(error, errorInfo) => {
        console.error('RecordDetail error:', error, errorInfo);
        // TODO: Send to analytics
      }}
    >
      <RecordDetailContent {...props} />
    </RecordDetailErrorBoundary>
  );
};

RecordDetail.propTypes = {
  recordId: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['modal', 'page']),
  initialData: PropTypes.object,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onShare: PropTypes.func,
  onLinkToRecord: PropTypes.func,
  initialOpenComments: PropTypes.bool,
  initialFocusTarget: PropTypes.shape({
    threadRootId: PropTypes.string,
    commentId: PropTypes.string
  }),
  suspendModalInteractions: PropTypes.bool
};

RecordDetailContent.propTypes = RecordDetail.propTypes;

export default RecordDetail;
